import { Button } from "@similarweb/ui-components";
import { BasicModal } from "@similarweb/ui-components";
import { PlainTooltip } from "@similarweb/ui-components";
import { useDidUpdate } from "app/custom-hooks/useDidUpdate";
import { translate } from "app/filters/i18nFilter";
import type { ChangeEvent } from "react";
import { useState } from "react";
import { compareTwoProfilesDTOsEqual } from "../contacts-search-profiles/helpers/common";
import { ProfileAccessLevel } from "../contacts-search-profiles/types/dto";
import { ProfileContent } from "./ProfileContent/ProfileContent";
import {
    CUSTOM_MODAL_STYLES,
    StyledButtonsContainer,
    StyledButtonsInnerContainer,
    StyledModalContent,
    StyledPrimaryButton,
    StyledTooltipText,
} from "./styles";
import type { ProfileModalProps } from "./types";

const DEFAULT_CREATE_TITLE_I18N_KEY = "si.account-overview.employee-profiles.create-profile.title";
const DEFAULT_EDIT_TITLE_I18N_KEY = "si.account-overview.employee-profiles.update-profile.title";
const DEFAULT_DESCRIPTION_I18N_KEY = "si.account-overview.employee-profiles.create-profile.description";

function ProfileModal(props: ProfileModalProps) {
    const {
        isOpened,
        profile,
        isBeingDeleted,
        isBeingSubmitted,
        createTitleI18nKey = DEFAULT_CREATE_TITLE_I18N_KEY,
        editTitleI18nKey = DEFAULT_EDIT_TITLE_I18N_KEY,
        descriptionI18nKey = DEFAULT_DESCRIPTION_I18N_KEY,
        onCancel,
        onSubmit,
        onDeleteClick,
    } = props;
    const [profileName, setProfileName] = useState("");
    const [selectedDepartments, selectDepartments] = useState<readonly string[]>([]);
    const [selectedSeniorities, selectSeniorities] = useState<readonly string[]>([]);
    const [includedTitles, setIncludedTitles] = useState<readonly string[]>([]);
    const [excludedTitles, setExcludedTitles] = useState<readonly string[]>([]);

    const isSelectionEmpty = [
        selectedDepartments.length === 0,
        selectedSeniorities.length === 0,
        includedTitles.length === 0,
        excludedTitles.length === 0,
    ].every(Boolean);

    const titleI18nKey = profile === null ? createTitleI18nKey : editTitleI18nKey;

    const submitButtonI18nKey = profile === null ? "si.common.button.create" : "si.common.button.apply";

    const primaryBtnTooltip =
        profile && !isSelectionEmpty
            ? "si.sidebar.contacts.persona-edit.edit.btn"
            : "si.sidebar.contacts.persona-edit.create.btn";

    useDidUpdate(() => {
        if (!isOpened) {
            setProfileName("");
            selectDepartments([]);
            selectSeniorities([]);
            setIncludedTitles([]);
            setExcludedTitles([]);
        }
    }, [isOpened]);

    useDidUpdate(() => {
        if (profile !== null && isOpened) {
            setProfileName(profile.name);
            selectDepartments(profile.departments);
            selectSeniorities(profile.seniority);
            setIncludedTitles(profile.includeTitles);
            setExcludedTitles(profile.excludeTitles);
        }
    }, [profile, isOpened]);

    function handleSubmit() {
        onSubmit({
            name: profileName,
            includeTitles: includedTitles,
            excludeTitles: excludedTitles,
            seniority: selectedSeniorities,
            departments: selectedDepartments,
            accessLevel: profile?.accessLevel ?? ProfileAccessLevel.User,
        });
    }

    function getIfSubmitIsDisabled() {
        if (isBeingSubmitted || isSelectionEmpty) {
            return true;
        }

        if (profile === null) {
            return profileName.trim().length < 3;
        }

        return compareTwoProfilesDTOsEqual(
            {
                name: profile.name,
                departments: profile.departments,
                seniority: profile.seniority,
                includeTitles: profile.includeTitles,
                excludeTitles: profile.excludeTitles,
                accessLevel: profile.accessLevel,
            },
            {
                name: profileName,
                departments: selectedDepartments,
                seniority: selectedSeniorities,
                includeTitles: includedTitles,
                excludeTitles: excludedTitles,
                accessLevel: profile.accessLevel,
            },
        );
    }

    function handleNameChange(event: ChangeEvent<HTMLInputElement>) {
        const {
            target: { value },
        } = event;

        setProfileName(value);
    }

    const isSubmitButtonDisabled = getIfSubmitIsDisabled();

    const renderFooter = (
        <StyledButtonsContainer>
            {profile !== null && (
                <Button
                    type="flatWarning"
                    onClick={onDeleteClick}
                    isLoading={isBeingDeleted}
                    isDisabled={isBeingDeleted}
                    dataAutomation="delete-button"
                >
                    {translate("si.common.button.delete")}
                </Button>
            )}

            <StyledButtonsInnerContainer>
                <Button
                    type="flat"
                    onClick={onCancel}
                    dataAutomation="cancel-button"
                >
                    {translate("si.common.button.cancel")}
                </Button>

                <PlainTooltip
                    enabled={isSubmitButtonDisabled}
                    placement="top"
                    tooltipContent={<StyledTooltipText>{translate(primaryBtnTooltip)}</StyledTooltipText>}
                    appendTo="#button-container"
                >
                    <StyledPrimaryButton id="button-container">
                        <Button
                            type="primary"
                            onClick={handleSubmit}
                            isLoading={isBeingSubmitted}
                            isDisabled={isSubmitButtonDisabled}
                            dataAutomation="submit-button"
                        >
                            {translate(submitButtonI18nKey)}
                        </Button>
                    </StyledPrimaryButton>
                </PlainTooltip>
            </StyledButtonsInnerContainer>
        </StyledButtonsContainer>
    );

    return (
        <BasicModal
            isOpen={isOpened}
            closeIconConfig={{
                show: false,
            }}
            shouldCloseOnOverlayClick={false}
            customStyles={CUSTOM_MODAL_STYLES}
        >
            <StyledModalContent data-automation="profile-modal">
                <ProfileContent
                    descriptionI18n={descriptionI18nKey}
                    excludedTitles={excludedTitles}
                    onNameChange={handleNameChange}
                    includedTitles={includedTitles}
                    profileName={profileName}
                    onChangeSeniorities={selectSeniorities}
                    onChangeDepartments={selectDepartments}
                    selectedDepartments={selectedDepartments}
                    selectedSeniorities={selectedSeniorities}
                    onChangeExcludedTitles={(excTitles: readonly string[]) => setExcludedTitles(excTitles)}
                    onChangeIncludedTitles={(incTitles: readonly string[]) => setIncludedTitles(incTitles)}
                    footerComponent={renderFooter}
                    titleI18nKey={titleI18nKey}
                />
            </StyledModalContent>
        </BasicModal>
    );
}

export default ProfileModal;
